import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectItem,
  Textarea,
} from "@porchpass/ui";
import { addCondition } from "src/api/preApproval";
import type {
  ConditionSection,
  PreApprovalNote,
} from "src/api/preApproval/types";

const SECTIONS: { value: ConditionSection; label: string }[] = [
  { value: "customer", label: "Customer Conditions" },
  { value: "funding", label: "Funding Conditions" },
  { value: "seller", label: "Seller Conditions" },
];

interface CreateConditionFromNoteModalProps {
  note: PreApprovalNote | null;
  initialSection?: ConditionSection;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function CreateConditionFromNoteModal({
  note,
  initialSection = "customer",
  open,
  onOpenChange,
  onSuccess,
}: CreateConditionFromNoteModalProps) {
  const { applicationId } = useParams<{ applicationId: string }>();
  const [section, setSection] = useState<ConditionSection>(initialSection);
  const [title, setTitle] = useState("");
  const [internalDescription, setInternalDescription] = useState("");
  const [externalDescription, setExternalDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (note && open) {
      setTitle(note.note.slice(0, 80));
      setInternalDescription(note.note);
      setExternalDescription("");
      setSection(initialSection);
      setShowSuccess(false);
      setError(null);
    }
  }, [note, open, initialSection]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!applicationId || !note || !title.trim()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await addCondition(applicationId, section, {
        title: title.trim(),
        internal_description: internalDescription.trim() || undefined,
        external_description: externalDescription.trim() || undefined,
      });
      setShowSuccess(true);
      setTimeout(() => {
        onOpenChange(false);
        onSuccess();
      }, 1200);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to create condition right now.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Condition from Note</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {showSuccess ? (
            <div className="py-8 text-center">
              <p className="font-medium text-[#1A7F4B]">
                Condition created successfully
              </p>
            </div>
          ) : (
            <form
              id="create-condition-from-note-form"
              onSubmit={(event) => {
                void handleSubmit(event);
              }}
              className="flex flex-col gap-4"
            >
              <Select
                label="Condition type"
                value={section}
                onValueChange={(value) => setSection(value as ConditionSection)}
              >
                {SECTIONS.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </Select>
              <Input
                label="Title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                required
                clearable={false}
              />
              <Textarea
                label="Internal description"
                value={internalDescription}
                onChange={(event) => setInternalDescription(event.target.value)}
                rows={4}
                showOptional
              />
              <Textarea
                label="External description"
                value={externalDescription}
                onChange={(event) => setExternalDescription(event.target.value)}
                rows={4}
                showOptional
              />
              {error && <p className="text-sm text-[#B91C1C]">{error}</p>}
            </form>
          )}
        </DialogBody>

        {!showSuccess && (
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="create-condition-from-note-form"
              disabled={isSubmitting || !title.trim()}
            >
              {isSubmitting ? "Creating..." : "Create Condition"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
